/* eslint-disable jsx-a11y/no-autofocus */
import React, { useState } from 'react';

import { Modal, Button, LoadingButton } from '@common/index';
import { Stack, TextField } from '@mui/material';
import { useTheme } from '../../styles/theme';

import { useDispatch, useSelector } from 'react-redux';
import { flowActions } from '@store/slices';
import { AppDispatch, State } from '@store/index';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteService } from '@requests/service';
import { getServices } from '@store/async/service';
import { Delete } from '@mui/icons-material';

export const DeleteService: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const queryClient = useQueryClient();
  const theme = useTheme();

  const [confirm, setConfirm] = useState('');

  const deleteServiceId = useSelector((state: State) => state.flow.deleteService);
  const setOpen = (val: string | null) => dispatch(flowActions.setDeleteService(val));

  const { enqueueSnackbar } = useSnackbar();

  const { mutateAsync: discard, status: deleteStatus } = useMutation({
    mutationFn: deleteService,
    onSuccess: (data) => {
      if (!data) return;

      if (data?.error) {
        enqueueSnackbar('Failed to delete workspace!', {
          variant: 'error',
          autoHideDuration: 10000,
        });

        enqueueSnackbar(data?.error?.error || data?.error, {
          variant: 'error',
          autoHideDuration: 10000,
        });

        return;
      }

      setOpen(null);
      setConfirm('');
      dispatch(getServices());
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      queryClient.invalidateQueries({ queryKey: ['globalFiles'] });

      enqueueSnackbar('Deleted workspace successfully!', { variant: 'success', autoHideDuration: 3000 });
    },
  });

  const isDeleteLoading = deleteStatus === 'pending';

  const deleteServiceByName = () => {
    if (!deleteServiceId) return;

    enqueueSnackbar('Deleting workspace...', {
      variant: 'info',
      autoHideDuration: 3000,
    });

    discard({ id: deleteServiceId });
  };

  return (
    <Modal
      header='Delete Workspace'
      identifier={deleteServiceId}
      open={Boolean(deleteServiceId)}
      setOpen={setOpen as (val: boolean | string | null) => void}
      background={theme.telnyx.red}
      icon={<Delete />}
      noBottomBorder
      info={
        'Warning! You are about to delete a workspace which will remove all workflows, buckets, secrets, permissions and reporting. This action CANNOT be undone. Please type the workspace name to confirm deletion.'
      }
      body={
        <TextField
          variant='standard'
          fullWidth
          required
          label='Workspace Name Confirmation'
          autoFocus
          value={confirm}
          error={Boolean(confirm && confirm !== deleteServiceId)}
          helperText={
            confirm && confirm !== deleteServiceId
              ? `The input must match the workspace name "${deleteServiceId}"`
              : undefined
          }
          placeholder={'Type the name of the workspace to confirm deletion.'}
          onChange={(e) => setConfirm(e.target.value)}
        />
      }
      actions={
        <Stack direction='row' spacing={1}>
          <Button small onClick={() => setOpen(null)} disabled={isDeleteLoading}>
            Back
          </Button>
          <LoadingButton
            small
            customColor='red'
            onClick={deleteServiceByName}
            loading={isDeleteLoading}
            minWidth='70px'
            disabled={confirm !== deleteServiceId}
          >
            Delete
          </LoadingButton>
        </Stack>
      }
    />
  );
};
